import { can, type Permission, type StaffRole } from '@atlas-control/shared';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createAuthClient } from 'better-auth/react';
import { api, isApiError } from './api';

/** Better Auth client for sign-in, sign-out, password reset/change and sessions. */
export const authClient = createAuthClient({ basePath: '/api/auth' });

export interface Me {
  user: { id: string; name: string; email: string; role: StaffRole };
  permissions: Permission[];
}

/** The signed-in user, or null when signed out. */
export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<Me | null> => {
    try {
      return await api<Me>('/me');
    } catch (err) {
      if (isApiError(err, 401)) return null;
      throw err;
    }
  },
  staleTime: 60_000,
});

/** Only for components under the authenticated layout, where `me` is guaranteed. */
export function useMe(): Me {
  const { data } = useSuspenseQuery(meQuery);
  if (!data) throw new Error('useMe() used outside the authenticated layout');
  return data;
}

export function useCan(permission: Permission): boolean {
  return can(useMe().user.role, permission);
}

export const ROLE_LABEL: Record<StaffRole, string> = {
  auditor: 'Auditor',
  admin: 'Admin',
  developer: 'Developer',
};
